import type { components } from "@/api/schema";

/**
 * The rules of a WhatsApp template, for the template manager.
 *
 * Kept apart from the form so each rule is one small function with a test,
 * instead of a condition buried in a render. The server applies the same
 * rules again (`TemplateWriteSerializer`); these say it before a click.
 */

export type ManagedTemplate = components["schemas"]["MessageTemplate"];
export type TemplateInput = components["schemas"]["TemplateWriteRequest"];
export type VariableSource = NonNullable<TemplateInput["variables"]>[number];
export type TemplateLanguage = NonNullable<TemplateInput["language"]>;
/** Authentication templates are one-time codes, which the CRM never sends. */
export type TemplateCategory = "UTILITY" | "MARKETING";
/** What fills a blank, or `""` while nobody has chosen. */
export type Slot = VariableSource | "";

export const SOURCES: readonly VariableSource[] = [
  "contact_name",
  "company_name",
  "agent_name",
];
export const LANGUAGES: readonly TemplateLanguage[] = ["es_MX", "es", "en_US"];
/** Meta allows more; three is what fits under a message on a phone. */
export const MAX_BUTTONS = 3;

const BLANK = /\{\{(\d+)\}\}/g;

/** "Retomar conversación" -> `retomar_conversacion`, the only name Meta accepts. */
export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120);
}

/** `Hola {{2}} y {{1}}` -> `[1, 2]`. Each blank once, in number order. */
export function blankNumbers(text: string): number[] {
  const numbers = new Set([...text.matchAll(BLANK)].map((match) => Number(match[1])));
  return [...numbers].sort((a, b) => a - b);
}

/** Put the next blank where the cursor is, and remember what fills it. */
export function insertBlank(
  body: string,
  at: number,
  slots: readonly Slot[],
  source: VariableSource,
): { body: string; variables: Slot[]; cursor: number } {
  const token = `{{${slots.length + 1}}}`;
  const position = Math.max(0, Math.min(at, body.length));
  return {
    body: body.slice(0, position) + token + body.slice(position),
    variables: [...slots, source],
    cursor: position + token.length,
  };
}

/** Take blank `n` out and close the gap, so the rest stay numbered in order. */
export function removeBlank(
  body: string,
  slots: readonly Slot[],
  n: number,
): { body: string; variables: Slot[] } {
  const renumbered = body.replace(BLANK, (match, raw: string) => {
    const number = Number(raw);
    if (number === n) return "";
    return number > n ? `{{${number - 1}}}` : match;
  });
  return {
    body: renumbered.replace(/ {2,}/g, " "),
    variables: slots.filter((_, index) => index !== n - 1),
  };
}

export type Problem =
  | "name"
  | "body"
  | "order"
  | "unassigned"
  | "edges"
  | "headerBlank"
  | "footerBlank"
  | "duplicateButtons";

/** What Meta's review, or the CRM, would refuse — said before anyone sends it. */
export function problemsIn(
  draft: {
    name: string;
    header: string;
    body: string;
    footer: string;
    buttons: readonly string[];
    variables: readonly Slot[];
  },
  creating: boolean,
): Problem[] {
  const problems: Problem[] = [];
  const body = draft.body.trim();
  const numbers = blankNumbers(body);
  const buttons = draft.buttons.map((text) => text.trim()).filter(Boolean);

  if (creating && !slugify(draft.name)) problems.push("name");
  if (!body) problems.push("body");
  if (numbers.some((number, index) => number !== index + 1)) problems.push("order");
  if (
    numbers.length !== draft.variables.length ||
    draft.variables.some((slot) => slot === "")
  ) {
    problems.push("unassigned");
  }
  if (body.startsWith("{{") || body.endsWith("}}")) problems.push("edges");
  if (draft.header.includes("{{")) problems.push("headerBlank");
  if (draft.footer.includes("{{")) problems.push("footerBlank");
  if (new Set(buttons).size !== buttons.length) problems.push("duplicateButtons");
  return problems;
}

/** The body cut into text and blanks, the blanks holding example values. */
export function previewParts(
  body: string,
  slots: readonly Slot[],
  example: (source: VariableSource) => string,
): { text: string; blank: boolean }[] {
  const parts: { text: string; blank: boolean }[] = [];
  let last = 0;
  for (const match of body.matchAll(BLANK)) {
    const index = match.index ?? 0;
    if (index > last) parts.push({ text: body.slice(last, index), blank: false });
    const slot = slots[Number(match[1]) - 1];
    parts.push({ text: slot ? example(slot) : match[0], blank: true });
    last = index + match[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last), blank: false });
  return parts;
}
